import React, { useState, useMemo } from 'react';
import { Task, OKRState } from '../types';
import { computeSmartScore, getTeamMemberInitials, getTeamMemberColor, taskStatusConfig, priorityColors, priorityLabels } from '../helpers';
import { useOKRPermissions } from '../useOKRPermissions';
import { Zap, Clock, Calendar, AlertTriangle, CheckCircle2, LayoutGrid, List, Users, Pencil } from 'lucide-react';

// ── Task card ────────────────────────────────────────────────────────────────

const TaskCard: React.FC<{ task: Task; state: OKRState; onStatusChange: (id: string, s: Task['status']) => void; onEdit?: (t: Task) => void }> = ({ task, state, onStatusChange, onEdit }) => {
    const score = computeSmartScore(task);
    const cfg = taskStatusConfig[task.status];
    const initials = getTeamMemberInitials(task.assigneeId, state);
    const avatarColor = getTeamMemberColor(task.assigneeId, state);
    const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'done';
    const scoreCls = score >= 15 ? 'text-red-600 bg-red-50' : score >= 8 ? 'text-orange-500 bg-orange-50' : 'text-gray-500 bg-gray-50';

    return (
        <div className={`rounded-xl border bg-white p-3.5 shadow-sm hover:shadow-md transition-shadow ${task.status === 'blocked' ? 'border-red-200' : 'border-gray-100'}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm font-semibold text-navy-700 leading-snug flex-1">{task.title}</p>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0 ${scoreCls}`}>
                    <Zap size={10} className="inline mr-0.5" />{score}
                </span>
            </div>

            {task.status === 'blocked' && task.blockedReason && (
                <div className="mb-2 flex items-start gap-1.5 rounded-lg bg-red-50 p-2 text-xs text-red-600">
                    <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /><span>{task.blockedReason}</span>
                </div>
            )}
            {task.businessImpact && task.status === 'done' && (
                <div className="mb-2 rounded-lg bg-green-50 p-2 text-xs text-green-700">
                    <CheckCircle2 size={12} className="inline mr-1" />
                    +{task.businessImpact.revenueEur.toLocaleString('fr-FR')}€ CA estimé
                </div>
            )}
            {(task.tags ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {task.tags!.map((t) => <span key={t} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
            )}

            <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: avatarColor }}>{initials}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${priorityColors[task.priority]}`}>{priorityLabels[task.priority]}</span>
                </div>
                <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    <Calendar size={11} />{new Date(task.dueDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                </span>
            </div>

            {task.estimatedHours > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-50">
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                        <span><Clock size={11} className="inline mr-1" />{task.spentHours}h / {task.estimatedHours}h</span>
                    </div>
                    <div className="w-full h-1 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, (task.spentHours / task.estimatedHours) * 100)}%`, backgroundColor: cfg.color }} />
                    </div>
                </div>
            )}

            <div className="mt-2 flex items-center gap-2">
                <select value={task.status} onChange={(e) => onStatusChange(task.id, e.target.value as Task['status'])} onClick={(e) => e.stopPropagation()} className="flex-1 text-xs rounded-lg border-0 py-1 px-2 font-medium cursor-pointer" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                    {Object.entries(taskStatusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                {onEdit && (
                    <button onClick={() => onEdit(task)} className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-toola-500 hover:border-toola-300 transition-colors flex-shrink-0" title="Modifier">
                        <Pencil size={12} />
                    </button>
                )}
            </div>
        </div>
    );
};

// ── Kanban columns ────────────────────────────────────────────────────────────

const COLS: { key: Task['status']; label: string }[] = [
    { key: 'todo', label: 'À faire' }, { key: 'in-progress', label: 'En cours' },
    { key: 'waiting', label: 'En attente' }, { key: 'blocked', label: 'Bloquée' }, { key: 'done', label: 'Terminée' },
];

// ── Board ────────────────────────────────────────────────────────────────────

const TaskBoard: React.FC<{
    tasks: Task[];
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
    onEditTask?: (task: Task) => void;
}> = ({ tasks, state, onStatusChange, onEditTask }) => {
    const { defaultToOwnTasks, currentUserId, currentUserName, isOwner } = useOKRPermissions();
    const [view, setView] = useState<'kanban' | 'list'>('kanban');
    const [sortBy, setSortBy] = useState<'impact' | 'dueDate' | 'priority'>('impact');
    // Non-owners default to their own tasks; can toggle to see all for assigning
    const [showAll, setShowAll] = useState(!defaultToOwnTasks);

    const visibleTasks = useMemo(() => {
        if (showAll || isOwner) return tasks;
        return tasks.filter((t) =>
            t.assigneeId === currentUserId ||
            state.team.find((m) => m.id === t.assigneeId)?.name === currentUserName,
        );
    }, [tasks, showAll, isOwner, currentUserId, currentUserName, state.team]);

    const sorted = useMemo(() => {
        const pOrd: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
        return [...visibleTasks].sort((a, b) => {
            if (sortBy === 'impact') return computeSmartScore(b) - computeSmartScore(a);
            if (sortBy === 'dueDate') return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            return (pOrd[a.priority] ?? 9) - (pOrd[b.priority] ?? 9);
        });
    }, [visibleTasks, sortBy]);

    return (
        <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                    {(['kanban', 'list'] as const).map((v) => (
                        <button key={v} onClick={() => setView(v)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === v ? 'bg-white shadow text-navy-700' : 'text-gray-500'}`}>
                            {v === 'kanban' ? <><LayoutGrid size={14} /> Kanban</> : <><List size={14} /> Liste</>}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    {!isOwner && (
                        <button
                            onClick={() => setShowAll((p) => !p)}
                            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${showAll ? 'border-toola-300 bg-blue-50 text-toola-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                        >
                            <Users size={13} />
                            {showAll ? 'Mes tâches' : 'Voir toutes (assigner)'}
                        </button>
                    )}
                    <span className="text-xs text-gray-500">Trier par</span>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-400">
                        <option value="impact">Score d'impact</option>
                        <option value="dueDate">Date limite</option>
                        <option value="priority">Priorité</option>
                    </select>
                </div>
            </div>

            {view === 'kanban' ? (
                <div className="overflow-x-auto pb-2">
                    <div className="flex gap-4" style={{ minWidth: '1000px' }}>
                        {COLS.map(({ key, label }) => {
                            const col = sorted.filter((t) => t.status === key);
                            const cfg = taskStatusConfig[key];
                            return (
                                <div key={key} className="flex-1 min-w-[190px]">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: cfg.color }}>{label}</span>
                                        <span className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{col.length}</span>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        {col.map((task) => <TaskCard key={task.id} task={task} state={state} onStatusChange={onStatusChange} onEdit={onEditTask} />)}
                                        {col.length === 0 && <div className="rounded-xl border-2 border-dashed border-gray-100 p-4 text-center text-xs text-gray-300">Aucune tâche</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {sorted.map((task) => {
                        const cfg = taskStatusConfig[task.status];
                        const score = computeSmartScore(task);
                        const initials = getTeamMemberInitials(task.assigneeId, state);
                        const avatarColor = getTeamMemberColor(task.assigneeId, state);
                        const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'done';
                        return (
                            <div key={task.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 hover:shadow-sm transition-shadow">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: avatarColor }}>{initials}</div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-navy-700 truncate">{task.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className={`text-xs border rounded-full px-2 py-0.5 ${priorityColors[task.priority]}`}>{priorityLabels[task.priority]}</span>
                                        {(task.tags ?? []).map((t) => <span key={t} className="text-xs text-gray-400 bg-gray-50 px-1.5 rounded">{t}</span>)}
                                    </div>
                                </div>
                                <span className={`text-xs flex items-center gap-1 flex-shrink-0 ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                                    <Calendar size={12} />{new Date(task.dueDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </span>
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-lg flex-shrink-0 ${score >= 15 ? 'text-red-600 bg-red-50' : score >= 8 ? 'text-orange-500 bg-orange-50' : 'text-gray-500 bg-gray-50'}`}>
                                    <Zap size={10} className="inline mr-0.5" />{score}
                                </span>
                                <select value={task.status} onChange={(e) => onStatusChange(task.id, e.target.value as Task['status'])} className="text-xs rounded-lg border-0 py-1 px-2 font-medium cursor-pointer flex-shrink-0" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                                    {Object.entries(taskStatusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                                {onEditTask && (
                                    <button onClick={() => onEditTask(task)} className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-toola-500 hover:border-toola-300 transition-colors flex-shrink-0" title="Modifier">
                                        <Pencil size={13} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {sorted.length === 0 && <div className="rounded-2xl border-2 border-dashed border-gray-100 p-12 text-center text-gray-300">Aucune tâche</div>}
                </div>
            )}
        </div>
    );
};

export default TaskBoard;
